import { DNDMetaContext } from '@components/dnd/base';
import { TraceKeyInput } from '@components/traceKeyInput/traceKeyInput';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@components/ui/dialog';
import useStores from '@hooks/useStore';
import type { AtomModel } from '@models/atom.model';
import { Checkbox } from '@mui/joy';
import type { DNDBlock } from '@stratumn/studio-shared';
import { Settings, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react';
import { type ReactNode, useContext } from 'react';
import styled, { css } from 'styled-components';

type EndControlsLineProps = {
  $isDisplayed: boolean;
};

const EndControlsLine = styled.div<EndControlsLineProps>`
  display: flex;
  flex-shrink: 0;
  flex-direction: row;
  gap: 0.6rem;
  align-items: center;
  user-select: none;
  opacity: 1;

  ${p =>
    !p.$isDisplayed &&
    css`
      opacity: 0;
    `}
`;

const Control = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const ICON_SIZE = 17;

export type SettingsConfig = {
  title: string;
  content: ReactNode;
};

type EndControlsProps = {
  hasDelete?: boolean;
  noDeleteCheck?: boolean;
  isHovering?: boolean;
  blockPath?: string;
  settings?: SettingsConfig;
  children?: ReactNode;
  dndBlock: DNDBlock;
} & (
  | {
      hasRequired: true;
      dataItem: AtomModel<{ required: boolean }>;
    }
  | {
      hasRequired: false;
      dataItem: AtomModel;
    }
);
const EndControlsFB = ({
  dndBlock,
  dataItem: atom,
  isHovering = true,
  hasDelete = true,
  noDeleteCheck = false,
  hasRequired,
  settings: settingsConfig,
  children
}: EndControlsProps) => {
  const { onDelete } = useContext(DNDMetaContext);

  const { modalStore } = useStores();

  const isDeletable = atom.isDeletable;

  const onDeleteClick = () => {
    if (!(noDeleteCheck || isDeletable)) {
      modalStore.referenceModal.open(
        'atom',
        atom.id,
        'referencedBy',
        atom.type
      );

      return;
    }
    onDelete(dndBlock.path);
  };

  return (
    <EndControlsLine $isDisplayed={isHovering}>
      {hasRequired && (
        <Checkbox
          checked={atom.data.required}
          color="neutral"
          label="Required"
          onChange={e => (atom.data.required = e.target.checked)}
          size="sm"
          variant="outlined"
        />
      )}
      <Control>{children}</Control>

      {hasDelete && (
        <Control data-tag="delete-control" onClick={onDeleteClick}>
          <Trash2 size={ICON_SIZE} />
        </Control>
      )}
      {settingsConfig && (
        <Dialog>
          <DialogTrigger>
            <Settings size={ICON_SIZE} />
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Settings — {settingsConfig.title}</DialogTitle>
              <DialogDescription>
                Manage <i className="font-semibold">{settingsConfig.title}</i>
                's settings.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <section className="flex h-full flex-col gap-6">
                {settingsConfig.content}
                <TraceKeyInput
                  parentName={atom.data.title as Maybe<string>}
                  // @ts-expect-error parentName is not a required prop
                  traceKey={atom.traceKey}
                />
              </section>
            </DialogBody>
          </DialogContent>
        </Dialog>
      )}
    </EndControlsLine>
  );
};

export default observer(EndControlsFB);
